import Layout from 'components/layout';

import React, { FC } from 'react';

import { getI18nData } from '@lucid/utils';
import { graphql } from 'gatsby';
import { Box, Quote, SimpleCta } from '@lucid/components-react';
import {
  Drupal_FieldNodeIndividualRoleFieldHeaderCta,
  Drupal_NodeIndividualRole,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFullCard,
  Drupal_ParagraphHeroSection,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphQuote,
  Drupal_ParagraphStickySlider
} from 'generated/drupal-graphql';
import { mqMax } from '@lucid/theme';

import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import Cards from 'components/cards/cards';
import Slider from 'components/sticky-slider/sticky-slider';
import SunriseHeader from 'components/sunrise-header/sunrise-header';
import { backgroundGrayPattern } from 'theme/shared';
import { PageTemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const Role: FC<PageTemplateProps> = ({ data, pageContext, location }) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeIndividualRole;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, shouldHideSsr } = pageContext;

  const i18n = getI18nData(i18nData, i18nKey);
  const headerCtas = (node.fieldHeaderCta as Drupal_FieldNodeIndividualRoleFieldHeaderCta[]).map(
    ({ entity }) => entity as Drupal_ParagraphCtaButton
  );

  const marketoFormEntity =
    headerCtas.length > 0 && headerCtas[0].fieldMarketo
      ? (headerCtas[0].fieldMarketo.entity as Drupal_ParagraphMarketoForm)
      : null;

  const headerSection = node.fieldHeaderSection
    ? (node.fieldHeaderSection.entity as Drupal_ParagraphHeroSection)
    : null;

  const fullTitle =
    node.fieldTitlePiece && node.fieldTitlePiece.length > 0
      ? (node.fieldTitlePiece as string[])
      : (headerSection?.fieldHeader as string);

  const slides = node
    .fieldStickySlider!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphStickySlider);

  const cards = node
    .fieldHowToSection!.filter(isDefined)
    .map(({ entity }) => entity as Drupal_ParagraphFullCard);

  const quote = node.fieldQuoteBundle
    ? (node.fieldQuoteBundle.entity as Drupal_ParagraphQuote)
    : null;

  return (
    <Layout
      entityTranslations={entityTranslations}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      shouldHideSsr={shouldHideSsr}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <Box
        as="main"
        css={[
          backgroundGrayPattern,
          { overflow: 'unset' }
        ]}
      >
        {headerSection && (
          <SunriseHeader
            breadCrumbHub={node.fieldRoleType!}
            breadCrumbPage={node.fieldTitle!}
            buttons={headerCtas}
            header={fullTitle}
            pageType={pageType}
            subheader={headerSection.fieldSubheader!}
          />
        )}
        <Slider
          buttonText={i18n('mktg_ct_all_get_a_quote')}
          mktoFormEntity={marketoFormEntity}
          sections={slides}
        />
        <Cards cards={cards} />
        {quote && <Quote bottomAttribution={quote.fieldAttribution} quote={quote.fieldQuote} />}
        <SimpleCta heading={i18n('mktg_ct_consideration_footer_header')}>
          <SignUpCta css={{ [mqMax.mobile]: { width: '100%' } }} />
        </SimpleCta>
      </Box>
    </Layout>
  );
};

export default Role;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeIndividualRole {
          ...entityFields
          fieldTitle
          fieldHeaderSection {
            entity {
              ... on Drupal_ParagraphHeroSection {
                fieldHeader
                fieldSubheader
              }
            }
          }
          fieldTitlePiece
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonLink
                fieldButtonText
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldHowToSection {
            entity {
              ... on Drupal_ParagraphFullCard {
                fieldImage
                fieldCtaHeading
                fieldLink
                fieldCardSubheader {
                  processed
                }
              }
            }
          }
          fieldQuoteBundle {
            entity {
              ... on Drupal_ParagraphQuote {
                fieldQuote
                fieldAttribution
              }
            }
          }
          fieldIsPrivate
          fieldRoleType
          fieldStickySlider {
            entity {
              ... on Drupal_ParagraphStickySlider {
                fieldHeader
                fieldBody {
                  processed
                }
                fieldImage
                fieldHideImageShadow
                fieldDoNotAddBrow
                fieldCta {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mktg_ct_consideration_footer_header
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
          }
        }
      }
    }
  }
`;
